import type { RowBrandFlag } from './importBrandRouting';

/**
 * The brand control on one bulk-import preview row. Rendered only when the
 * importing brand has acquired siblings a row could belong to instead — see
 * importBrandRouting.ts.
 *
 * Two states, and the second is deliberately not a greyed-out dropdown: a
 * disabled control says "you can't" without saying why, and the why here is
 * something the curator can act on somewhere else.
 *
 *   - a row not imported before: a dropdown, pre-set to the detected brand;
 *   - a row imported before: where it lives, and where to go to change that.
 */

export interface ImportBrandOption {
  id: string;
  label: string;
}

interface ImportBrandCellProps {
  /** Shown to assistive tech: which row this control is for. */
  reference: string;
  options: ImportBrandOption[];
  /** The brand the row is, or will be, under. */
  brandId: string;
  /** Short name of the brand the row already lives under, when it was imported before. */
  lockedBrandName?: string;
  /** The brand detection chose, for the "changed by you" note. */
  detectedBrandId: string;
  flags: RowBrandFlag[];
  /** Names used in the flag text. */
  detectedBrandName: string;
  onChange: (brandId: string) => void;
}

function flagText(flag: RowBrandFlag, detectedBrandName: string, lockedBrandName?: string): string {
  switch (flag) {
    case 'unknown-vendor':
      return 'Vendor is not a known brand — check the brand';
    case 'vendor-title-disagree':
      return 'Vendor and title disagree — check the brand';
    case 'existing-elsewhere':
      return `Already under ${lockedBrandName ?? 'another brand'}; detection says ${detectedBrandName}`;
    case 'split':
      return 'This product has references under more than one brand';
  }
}

export function ImportBrandCell({
  reference,
  options,
  brandId,
  lockedBrandName,
  detectedBrandId,
  flags,
  detectedBrandName,
  onChange,
}: ImportBrandCellProps) {
  return (
    <div className="mt-1 text-[11px] leading-tight" data-testid="import-brand-cell">
      {lockedBrandName ? (
        <p className="text-gray-600">
          <span className="font-semibold text-gray-700">Brand:</span> {lockedBrandName}
          <span className="block text-gray-500">
            Cannot change — already imported under {lockedBrandName}. Change in brand editor if
            needed.
          </span>
        </p>
      ) : (
        <label className="flex items-center gap-1.5">
          <span className="font-semibold text-gray-700">Brand:</span>
          <select
            aria-label={`Brand for ${reference}`}
            value={brandId}
            onChange={(e) => onChange(e.target.value)}
            className="rounded border-gray-300 py-0.5 pl-1.5 pr-6 text-[11px] focus:border-brand-500 focus:ring-brand-500"
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          {brandId !== detectedBrandId && (
            <span className="text-gray-500">changed by you (detected {detectedBrandName})</span>
          )}
        </label>
      )}
      {flags.map((flag) => (
        <span
          key={flag}
          className="mt-0.5 block rounded bg-amber-50 px-1.5 py-0.5 text-amber-800 border border-amber-200"
        >
          ⚠ {flagText(flag, detectedBrandName, lockedBrandName)}
        </span>
      ))}
    </div>
  );
}
